/**
 * Explicit one-shot browser geolocation. Does not run on mount.
 * Returns a candidate pin only. Does not write the destination store,
 * persist an address, or call checkout. No HTTP.
 */

import type { DestinationCoordinates } from "@/domain/destination/destination-coordinates.types"
import {
  GEOLOCATION_TIMEOUT_MS,
  type BrowserLocationFailureKind,
  type BrowserLocationResult,
} from "@/domain/destination/browser-location.types"
import { sanitizeAccuracyMeters } from "@/domain/destination/location-accuracy"

export { GEOLOCATION_TIMEOUT_MS }
export type {
  BrowserLocationFailureKind,
  BrowserLocationFailure,
  BrowserLocationResult,
  BrowserLocationSuccess,
} from "@/domain/destination/browser-location.types"

const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: GEOLOCATION_TIMEOUT_MS,
  maximumAge: 0,
}

let nextRequestId = 0
let activeRequestId = 0

function fail(kind: BrowserLocationFailureKind): BrowserLocationResult {
  return { ok: false, kind }
}

function isCurrentRequest(requestId: number): boolean {
  return requestId === activeRequestId && requestId !== 0
}

function readGeolocation(): Geolocation | null {
  if (typeof navigator === "undefined") return null
  const geo = navigator.geolocation
  if (!geo || typeof geo.getCurrentPosition !== "function") return null
  return geo
}

export function isWgs84Coordinate(latitude: number, longitude: number): boolean {
  return (
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  )
}

function mapPositionError(code: number | undefined): BrowserLocationFailureKind {
  if (code === 1) return "permission_denied"
  if (code === 3) return "timeout"
  return "position_unavailable"
}

/**
 * Drops in-flight getCurrentPosition callbacks so they cannot resolve as
 * success after restaurant switch, tenant loss, or logout. Does not start a
 * new request. Does not write the destination store.
 */
export function abandonPendingLocationRequests(): void {
  activeRequestId = 0
}

export function resetBrowserLocationRequestsForTests(): void {
  nextRequestId = 0
  activeRequestId = 0
}

/**
 * User-initiated one-shot location. Callers must pass the current restaurant id.
 * Latest explicit request wins; stale callbacks resolve as `{ ok: false, kind: "stale" }`.
 * Success returns candidate coordinates only — it does not write destination state.
 */
export function requestCurrentLocation(
  restaurantId: string
): Promise<BrowserLocationResult> {
  const rid = restaurantId.trim()
  if (!rid) {
    return Promise.resolve(fail("invalid_coordinates"))
  }

  const geo = readGeolocation()
  if (!geo) {
    return Promise.resolve(fail("unsupported"))
  }

  const requestId = ++nextRequestId
  activeRequestId = requestId

  return new Promise((resolve) => {
    const finish = (result: BrowserLocationResult) => {
      if (!isCurrentRequest(requestId)) {
        resolve(fail("stale"))
        return
      }
      resolve(result)
    }

    try {
      geo.getCurrentPosition(
        (position) => {
          if (!isCurrentRequest(requestId)) {
            resolve(fail("stale"))
            return
          }

          const latitude = position?.coords?.latitude
          const longitude = position?.coords?.longitude
          if (
            typeof latitude !== "number" ||
            typeof longitude !== "number" ||
            !isWgs84Coordinate(latitude, longitude)
          ) {
            finish(fail("invalid_coordinates"))
            return
          }

          const coordinates: DestinationCoordinates = {
            restaurantId: rid,
            latitude,
            longitude,
          }
          // Accuracy is advisory: unusable values become undefined, never a failure.
          const accuracyMeters = sanitizeAccuracyMeters(position?.coords?.accuracy)
          finish(
            accuracyMeters === undefined
              ? { ok: true, coordinates }
              : { ok: true, coordinates, accuracyMeters }
          )
        },
        (error) => {
          if (!isCurrentRequest(requestId)) {
            resolve(fail("stale"))
            return
          }
          finish(fail(mapPositionError(error?.code)))
        },
        GEOLOCATION_OPTIONS
      )
    } catch {
      finish(fail("unsupported"))
    }
  })
}
